import React, { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Flex, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { DayButton, DayPicker } from "react-day-picker"
import "react-day-picker/style.css"
import { numberToCurrency } from "helpers/string"
import { isExperienceDateUnavailable, isMultiDayStartUnavailable } from "utils/experienceAvailability"
import CalendarHeader from "./CalendarHeader"
import PanelSection from "./PanelSection"
import styles from "./BookingPanel.module.css"

// Inline month calendar. Unavailable days come from the experience itself (same rules as the old
// widget) and are greyed. For a multi-day trip only start dates whose whole span is free can be
// picked: a free day whose span runs into an unavailable day isn't greyed, but can't be picked and
// says so in a tooltip. The span is shaded once a start date is chosen. A ticketed trip passes `bookableDates` instead (the
// days with a start time left), and the calendar opens on the first of them, plus `discountByDate`
// for the discounted-day markers and `getDayPrices` for their price tooltip. The month is changed
// with the header's arrows or its month grid
// (CalendarHeader).
// The month and arrows are drawn by CalendarHeader instead.
const HiddenCaption = () => null

const DiscountsContext = createContext(null)

// react-day-picker's DayButton keeps its button ref to itself; the price tooltip needs it too.
const RefDayButton = forwardRef(({ day, modifiers, ...buttonProps }, ref) => {
  const ownRef = useRef(null)
  useEffect(() => {
    if (modifiers.focused) ownRef.current?.focus()
  }, [modifiers.focused])
  const setRefs = (node) => {
    ownRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }
  return <button ref={setRefs} {...buttonProps} />
})

RefDayButton.propTypes = {
  day: PropTypes.object.isRequired,
  modifiers: PropTypes.shape({ focused: PropTypes.bool }).isRequired
}

// The design's padding and corners; Ant Design's own tooltip styles outrank a class here.
const TOOLTIP_STYLES = { body: { padding: "var(--spacing-xs) var(--spacing-m)", borderRadius: "var(--border-radius-xs)" } }

// "Price per person:" then each type's price that day, struck price first when discounted.
const DayPrices = ({ prices }) => {
  const { t } = useTranslation()
  return (
    <Flex vertical className={styles.dayPrices}>
      <span>{t("booking_widget.experience.calendar.price_per_person")}</span>
      {prices.map((line) => (
        <span key={line.id}>
          {line.title}{" "}
          {line.originalPrice != null && <s>{numberToCurrency(line.originalPrice)}</s>} <strong>{numberToCurrency(line.price)}</strong>
        </span>
      ))}
    </Flex>
  )
}

DayPrices.propTypes = {
  prices: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), title: PropTypes.string, price: PropTypes.number, originalPrice: PropTypes.number })
  ).isRequired
}

// A private multi-day trip's free day that can't be a start date (its trip would run into an
// unavailable day): not greyed, but it can't be picked (aria-disabled, ignored on select), and the
// tooltip says why on hover, focus or tap.
const NoStartDayButton = (props) => {
  const { t } = useTranslation()
  const { spanDays } = useContext(DiscountsContext) || {}
  return (
    <Tooltip
      title={t("booking_widget.experience.calendar.no_start", { days: spanDays })}
      color="var(--color-surface-dark)"
      trigger={["hover", "focus", "click"]}
      styles={TOOLTIP_STYLES}
    >
      <RefDayButton {...props} aria-disabled="true" />
    </Tooltip>
  )
}

NoStartDayButton.propTypes = {
  day: PropTypes.object.isRequired,
  modifiers: PropTypes.object.isRequired
}

// A bookable day with a discount gets a dot by its number and "-20%" under it, and, when the
// calendar is given each day's prices, a "Price per person" tooltip on hover or focus.
const DiscountDayButton = (props) => {
  const { t } = useTranslation()
  const { discountByDate, getDayPrices } = useContext(DiscountsContext) || {}
  const date = dayjs(props.day.date).format("YYYY-MM-DD")
  if (props.modifiers.noStart) return <NoStartDayButton {...props} />
  const percent = props.modifiers.disabled ? null : discountByDate?.[date]
  if (!percent) return <DayButton {...props} />
  const content = (
    <>
      {props.children}
      <span className={styles.discountDot} aria-hidden="true" />
    </>
  )
  return (
    <>
      {getDayPrices ? (
        <Tooltip
          title={<DayPrices prices={getDayPrices(date)} />}
          placement="topLeft"
          arrow={{ pointAtCenter: true }}
          color="var(--color-surface-dark)"
          trigger={["hover", "focus"]}
          styles={TOOLTIP_STYLES}
        >
          <RefDayButton {...props}>{content}</RefDayButton>
        </Tooltip>
      ) : (
        <DayButton {...props}>{content}</DayButton>
      )}
      <span className={styles.discountLabel}>{t("booking_widget.experience.calendar.discount", { percent })}</span>
    </>
  )
}

DiscountDayButton.propTypes = {
  day: PropTypes.shape({ date: PropTypes.instanceOf(Date).isRequired }).isRequired,
  modifiers: PropTypes.shape({ disabled: PropTypes.bool, noStart: PropTypes.bool }).isRequired,
  children: PropTypes.node
}

DiscountDayButton.defaultProps = {
  children: null
}

const CALENDAR_COMPONENTS = { MonthCaption: HiddenCaption, DayButton: DiscountDayButton }

const DateSection = ({ experience, value, onChange, error, spanDays, bookableDates, discountByDate, getDayPrices, onMonthChange }) => {
  const { t } = useTranslation()
  const isMultiDay = spanDays > 1
  // The guide's time off (whole days). A ticketed multi-day start date is offered only while no day
  // of its trip is one of them: the API checks the same, and this also covers a month whose bookable
  // days haven't arrived yet (the trip's own start dates stand in).
  const blockedDates = useMemo(() => new Set(experience?.unavailable_dates || []), [experience])
  const isDisabled = useCallback(
    (day) => {
      if (bookableDates) {
        if (!bookableDates.includes(dayjs(day).format("YYYY-MM-DD"))) return true
        return isMultiDay && Array.from({ length: spanDays }, (_, index) => dayjs(day).add(index, "day").format("YYYY-MM-DD")).some((date) => blockedDates.has(date))
      }
      return isExperienceDateUnavailable(experience, day)
    },
    [experience, isMultiDay, spanDays, bookableDates, blockedDates]
  )
  // A private multi-day trip: a free day whose trip would run into an unavailable day can't be a
  // start date, but only the unavailable days themselves are greyed; this one says why instead.
  const isNoStart = useCallback(
    (day) => !bookableDates && isMultiDay && !isExperienceDateUnavailable(experience, day) && isMultiDayStartUnavailable(experience, day, spanDays),
    [experience, isMultiDay, spanDays, bookableDates]
  )
  const modifiers = useMemo(() => {
    const span = isMultiDay && value ? { from: dayjs(value).toDate(), to: dayjs(value).add(spanDays - 1, "day").toDate() } : null
    return { noStart: isNoStart, ...(span && { inSpan: span, spanEnd: span.to }) }
  }, [isMultiDay, value, spanDays, isNoStart])
  const selected = value ? dayjs(value).toDate() : undefined
  const firstBookable = bookableDates?.length ? dayjs(bookableDates[0]).startOf("month").toDate() : null
  const firstMonth = dayjs().startOf("month").toDate()
  const [month, setMonth] = useState(() => selected || firstBookable || firstMonth)
  // The month on show, for a parent that loads its bookable days month by month.
  useEffect(() => {
    if (onMonthChange) onMonthChange(month)
  }, [month, onMonthChange])
  const hasDiscounts = !!discountByDate && Object.keys(discountByDate).length > 0
  const discounts = useMemo(() => ({ discountByDate, getDayPrices, spanDays }), [discountByDate, getDayPrices, spanDays])

  return (
    <PanelSection
      title={t(isMultiDay ? "booking_widget.experience.select_start_date" : "booking_widget.experience.select_date")}
      subtitle={
        hasDiscounts ? (
          <Flex align="center" gap={6} className={styles.discountLegend}>
            <span className={styles.discountLegendDot} aria-hidden="true" />
            {t("booking_widget.experience.calendar.discounted_days")}
          </Flex>
        ) : null
      }
      error={error}
    >
      <CalendarHeader month={month} firstMonth={firstMonth} onMonthChange={setMonth}>
        <div className={`${styles.calendar} ${hasDiscounts ? styles.calendarWithDiscounts : ""}`}>
          <DiscountsContext.Provider value={discounts}>
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={(day) => day && !isNoStart(day) && onChange(dayjs(day).format("YYYY-MM-DD"))}
              disabled={isDisabled}
              modifiers={modifiers}
              modifiersClassNames={{ inSpan: styles.spanDay, spanEnd: styles.spanEnd, noStart: styles.noStartDay }}
              month={month}
              onMonthChange={setMonth}
              startMonth={firstMonth}
              hideNavigation
              components={CALENDAR_COMPONENTS}
              showOutsideDays
            />
          </DiscountsContext.Provider>
        </div>
      </CalendarHeader>
    </PanelSection>
  )
}

DateSection.propTypes = {
  experience: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  spanDays: PropTypes.number,
  bookableDates: PropTypes.arrayOf(PropTypes.string),
  discountByDate: PropTypes.objectOf(PropTypes.number),
  getDayPrices: PropTypes.func,
  onMonthChange: PropTypes.func
}

DateSection.defaultProps = {
  experience: null,
  value: null,
  error: null,
  spanDays: 1,
  bookableDates: null,
  discountByDate: null,
  getDayPrices: null,
  onMonthChange: null
}

export default DateSection
